import { useCallback, useEffect, useState } from "react";

import { useProfile } from "@/context/ProfileContext";
import { useConnectedApps } from "@/hooks/queries/use-apps";

import { setupReminders } from "./registry";
import {
  setupReminderDismissKey,
  setupReminderSnoozeKey,
  type SetupReminderView,
} from "./types";

export type ActiveSetupReminder = {
  id: string;
  view: SetupReminderView;
};

export interface SetupReminderResult {
  /** Highest-priority applicable reminder, or null when there's nothing to show. */
  active: ActiveSetupReminder | null;
  /** Persist "don't show again" (durable, per browser + business). */
  dismissForever: (reminderId: string) => void;
  /** Persist "maybe later" (session-scoped). Also used when the modal is closed. */
  snooze: (reminderId: string) => void;
}

function readKey(storage: Storage, key: string): boolean {
  try {
    return storage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeKey(storage: Storage, key: string): boolean {
  try {
    storage.setItem(key, "true");
    return true;
  } catch {
    return false;
  }
}

/**
 * Decides which invasive setup reminder (if any) to surface. Only owners and
 * admins are nagged — filling in business info and connecting integrations are
 * account-owner tasks. Reminders are evaluated in priority order and only one
 * is shown at a time; a permanently dismissed or session-snoozed reminder is
 * skipped. Defers (returns no reminder) while data a candidate depends on is
 * still loading, so the modal never flashes the wrong content.
 */
export function useSetupReminder(): SetupReminderResult {
  const { businessProfile, memberRole } = useProfile();
  const businessId = businessProfile?.uid;
  const canManageSetup = memberRole === "owner" || memberRole === "admin";

  const connectedAppsQuery = useConnectedApps(
    canManageSetup ? businessId : undefined,
  );

  // Dismissed/snoozed reminder ids, mirrored from storage into React state so
  // `active` recomputes when the user dismisses or snoozes one.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [snoozedIds, setSnoozedIds] = useState<Set<string>>(() => new Set());

  // Load persisted dismissals once the business is known (and reset on switch).
  useEffect(() => {
    if (!businessId) {
      setDismissedIds(new Set());
      setSnoozedIds(new Set());
      return;
    }
    const dismissed = new Set<string>();
    const snoozed = new Set<string>();
    for (const reminder of setupReminders) {
      if (readKey(localStorage, setupReminderDismissKey(businessId, reminder.id))) {
        dismissed.add(reminder.id);
      }
      if (readKey(sessionStorage, setupReminderSnoozeKey(reminder.id))) {
        snoozed.add(reminder.id);
      }
    }
    setDismissedIds(dismissed);
    setSnoozedIds(snoozed);
  }, [businessId]);

  const connectedAppsCount = connectedAppsQuery.isPending
    ? null
    : (connectedAppsQuery.data ?? []).filter((app) => app.connected).length;

  // Plain computation — the React Compiler memoizes it from the values read
  // below. Reminders are evaluated highest-priority first; only one shows.
  let active: ActiveSetupReminder | null = null;
  if (businessProfile && businessId && canManageSetup) {
    const ctx = { businessProfile, connectedAppsCount };
    const ordered = [...setupReminders].sort((a, b) => b.priority - a.priority);

    for (const reminder of ordered) {
      if (snoozedIds.has(reminder.id) || dismissedIds.has(reminder.id)) {
        continue;
      }

      const result = reminder.evaluate(ctx);
      // Data needed to decide this (higher-priority) reminder isn't in yet —
      // defer entirely rather than showing a lower-priority one that might be
      // superseded a moment later.
      if (result === "pending") {
        active = null;
        break;
      }
      if (result) {
        active = { id: reminder.id, view: result };
        break;
      }
    }
  }

  const dismissForever = useCallback(
    (reminderId: string) => {
      if (!businessId) return;
      const stored = writeKey(
        localStorage,
        setupReminderDismissKey(businessId, reminderId),
      );
      if (stored) {
        setDismissedIds((prev) => new Set(prev).add(reminderId));
      } else {
        // Storage unavailable (private mode) — fall back to session snooze.
        writeKey(sessionStorage, setupReminderSnoozeKey(reminderId));
        setSnoozedIds((prev) => new Set(prev).add(reminderId));
      }
    },
    [businessId],
  );

  const snooze = useCallback((reminderId: string) => {
    writeKey(sessionStorage, setupReminderSnoozeKey(reminderId));
    setSnoozedIds((prev) => new Set(prev).add(reminderId));
  }, []);

  return { active, dismissForever, snooze };
}
